// ============================================================================
// client/drapeau-papier.ts — LE DRAPEAU-CRAPAUD (05/08, 1er objet versus)
// ----------------------------------------------------------------------------
// Le fanion baladeur qui transforme en crapaud. Papier découpé comme tout le
// reste : un mât de kraft penché par le vent, un fanion crème qui CLAQUE
// (deux temps de sinus sur le tick — état partagé, jamais l'horloge murale en
// jeu), et l'écusson du crapaud dessus : le rond vert aux yeux SUR le crâne,
// la signature du crapaud de pet-papier — on sait ce qu'on attrape.
// Dessiné en direct (une quinzaine d'ordres canvas, 1-2 fanions par carte) :
// aucun cache nécessaire, et le flottement reste continu.
// ============================================================================

import { decoupe, rgba } from './papier'

type Ctx = CanvasRenderingContext2D

const KRAFT = '#a06a34'          // le mât : même kraft que la corde-objet
const PAPIER = '#f2e4cc'         // le fanion : crème du papier
const VERT = '#57b06a'           // l'écusson : vert du crapaud
const VERT_FONCE = '#2e6e42'

/** Le fanion, dessiné dans une case S×S posée en (x, y). `t` anime le claquement
 *  (passer le tick du jeu ; une valeur fixe fige — c'est ce que fait l'éditeur). */
export function drapeauPapier(g: Ctx, x: number, y: number, S: number, t: number): void {
  const k = S / 48
  const claque = Math.sin(t * 0.29) * 4 * k          // le bout du fanion bat au vent
  const ondule = Math.sin(t * 0.29 + 1.3) * 2 * k    // le ventre suit avec un temps de retard
  const mx = x + S * 0.30, my = y + S * 0.86         // pied du mât, bas de case
  const hx = mx + 7 * k, hy = my - 34 * k            // tête du mât (penché : le vent l'emporte)
  const boite = { x: x - S, y: y - S, w: S * 3, h: S * 3 }
  g.save()
  // Le mât : un tuteur de kraft, légèrement penché.
  decoupe(g, () => {
    g.beginPath()
    g.moveTo(mx - 1.8 * k, my); g.lineTo(hx - 1.6 * k, hy)
    g.lineTo(hx + 1.6 * k, hy); g.lineTo(mx + 1.8 * k, my)
    g.closePath()
  }, KRAFT, 'grain', boite, 2, 3)
  // Le pommeau, pour finir le mât proprement.
  g.fillStyle = KRAFT
  g.beginPath(); g.arc(hx, hy - 1 * k, 2.6 * k, 0, 7); g.fill()
  // Le fanion : un GRAND triangle de papier accroché sous la tête, qui claque vers la droite.
  // C'est un objet qu'on CHASSE : après le héros, il a le droit d'être le plus voyant.
  decoupe(g, () => {
    g.beginPath()
    g.moveTo(hx, hy)
    g.quadraticCurveTo(hx + 16 * k, hy + 3 * k + ondule, hx + 32 * k, hy + 7 * k + claque)
    g.lineTo(hx + 3 * k, hy + 18 * k + ondule * 0.5)
    g.closePath()
  }, PAPIER, 'plis', boite, 2, 3)
  // Le liseré vert sur tout le bord libre : c'est lui qui détache le fanion des fonds clairs.
  g.strokeStyle = rgba(VERT_FONCE, 0.75); g.lineWidth = Math.max(1.2, 1.8 * k)
  g.beginPath()
  g.moveTo(hx + 0.5 * k, hy + 1 * k)
  g.quadraticCurveTo(hx + 16 * k, hy + 4 * k + ondule, hx + 31 * k, hy + 7 * k + claque)
  g.lineTo(hx + 3.5 * k, hy + 17 * k + ondule * 0.5)
  g.closePath(); g.stroke()
  // L'écusson du crapaud : le rond vert, et les DEUX YEUX SUR LE CRÂNE (la
  // signature du crapaud de pet-papier — c'est le profil qui fait crapaud).
  const ex = hx + 12 * k, ey = hy + 8.5 * k + ondule * 0.6
  g.fillStyle = VERT
  g.beginPath(); g.arc(ex, ey, 6.6 * k, 0, 7); g.fill()
  g.fillStyle = PAPIER
  g.beginPath(); g.arc(ex - 2.8 * k, ey - 5.3 * k, 2.4 * k, 0, 7); g.fill()
  g.beginPath(); g.arc(ex + 2.8 * k, ey - 5.3 * k, 2.4 * k, 0, 7); g.fill()
  g.fillStyle = VERT_FONCE
  g.beginPath(); g.arc(ex - 2.8 * k, ey - 5.3 * k, 1.1 * k, 0, 7); g.fill()
  g.beginPath(); g.arc(ex + 2.8 * k, ey - 5.3 * k, 1.1 * k, 0, 7); g.fill()
  g.restore()
}
